import { analyticsParamsStore } from '@/stores/analytics';
import { ERROR_MESSAGES } from './translations/errorMessages';
import { getOptions, COUNTRIES } from './select-values';
import { INPUT_LABELS } from './translations/inputLabels';
import { LongForm, ShortForm, ContactFormOptions } from './forms';
import { useAppForm, useContactFormQuery } from './hooks/form';
import { useState, useEffect, type FormEvent, useMemo } from 'react';
import { useStore } from '@nanostores/react';
import type { ContactFormProps } from './FormProvider';
import type { MetaValues } from '@/types/form';
import { getTkValues } from './lib/getFormFormatedValues';
import { ChineseForm } from './forms/chineseForm';
import { FORM_HELP_OPTIONS } from './translations';

interface Location {
  hostname: string;
  pathname: string;
}

export const ContactForm = ({
  conversionType,
  dataSource,
  formType,
  language,
  submitText,
  tfValue,
  thankYouPage,
  defaultHowCanWeHelp,
}: ContactFormProps) => {
  const [sortedItems] = useState(getOptions(COUNTRIES, language));
  const [location, setLocation] = useState<Location>({
    hostname: '',
    pathname: '',
  });
  const { siteEntryPoint, pageParams } = useStore(analyticsParamsStore);

  const { mutateAsync, isError } = useContactFormQuery(language, thankYouPage);

  const contactFormOptions = useMemo(() => {
    const baseContactFormOptions = { ...ContactFormOptions }; // Create a shallow copy
    if (defaultHowCanWeHelp) {
      const defaultHowCanWeHelpTF = FORM_HELP_OPTIONS.find(
        (option) => option.en === defaultHowCanWeHelp,
      );
      baseContactFormOptions.defaultValues = {
        ...baseContactFormOptions.defaultValues, // Copy existing defaultValues
        howCanWeHelp: defaultHowCanWeHelpTF?.valueOverride || '',
      };
    }
    return baseContactFormOptions;
  }, [defaultHowCanWeHelp]);

  const form = useAppForm({
    ...contactFormOptions,
    onSubmitMeta: {} as MetaValues,
    onSubmit: async ({ value, meta }) => mutateAsync({ value, meta }),
  });

  useEffect(() => {
    if (location.hostname === '' || location.pathname === '') {
      setLocation({
        hostname: window.location.hostname,
        pathname: window.location.pathname,
      });
    }
  }, [location]);

  const formFields = useMemo(() => {
    if (language === 'zh') {
      return (
        <ChineseForm form={form} sortedCountries={sortedItems} language="zh" />
      );
    }
    if (formType === 'compact') {
      return (
        <ShortForm
          form={form}
          sortedCountries={sortedItems}
          language={language}
        />
      );
    }
    return (
      <>
        <ShortForm
          form={form}
          sortedCountries={sortedItems}
          language={language}
        />
        <LongForm form={form} language={language} />
      </>
    );
  }, [form, formType, language, sortedItems]);

  return (
    <form
      onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit({
          ...getTkValues(tfValue),
          Conversion_Entry_Page_Most_Recent: siteEntryPoint,
          Conversion_Page___Most_Recent:
            location ? `${location.hostname}${location.pathname}` : '',
          Conversion_Type_Most_Recent: conversionType || '',
          data_source: dataSource,
          Display_options: formType === 'standard',
          UTM_Medium___Most_Recent: pageParams.utm_medium || '',
          worker_classification_results:
            pageParams.worker_classification_results || '',
          worker_classification_score:
            pageParams.worker_classification_score || '',
        });
      }}
      className="@container flex flex-col"
    >
      {isError && (
        <div className="text-brand-error relative pb-4 text-base font-bold">
          {ERROR_MESSAGES.general[language]}
        </div>
      )}
      {formFields}
      {language !== 'zh' && (
        <>
          <div className="mb-9">
            <form.AppField name="Additional_Information">
              {(field) => (
                <field.TextArea label={INPUT_LABELS.anythingElse[language]} />
              )}
            </form.AppField>
          </div>
          <div className="mb-9">
            <form.AppField name="Opt_In">
              {(field) => (
                <field.ConsentCheckbox
                  copy={INPUT_LABELS.emailMarketing[language]}
                />
              )}
            </form.AppField>
          </div>
        </>
      )}
      <form.AppForm>
        <form.SubmitButton
          label={submitText || INPUT_LABELS.submit[language]}
        />
      </form.AppForm>
    </form>
  );
};
